// Seedable PRNG for deterministic transforms.
//
// Every transform that adds randomness — humanize, shuffle — takes a
// seed so the user can hit the same command twice and either get the
// same result (same seed) or a fresh one (incremented seed). The
// runtime always passes a fresh seed; tests pin one to assert
// determinism without relying on Math.random.

export type Rng = () => number;

// mulberry32 — a one-line PRNG with good distribution for this use
// case. Returns numbers in [0, 1).
export function mulberry32(seed: number): Rng {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Pick an int in [lo, hi] inclusive.
export function rngInt(rng: Rng, lo: number, hi: number): number {
  return Math.floor(rng() * (hi - lo + 1)) + lo;
}

// Fisher-Yates shuffle in place. Returns the same array for chaining.
export function rngShuffle<T>(rng: Rng, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [items[i]!, items[j]!] = [items[j]!, items[i]!];
  }
  return items;
}
